// CompareMechanicalView - Side-by-side mechanical property comparison TABLE
import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { getCellNormalized } from '../../../modules/dataService.js'
import { COMPARE_MECHANICAL } from '../../../data/definitions.js'

// Section icons (matching FactSheetSection style)
const FormFactorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="18" height="12" rx="2" ry="2"></rect>
    <circle cx="7" cy="12" r="2"></circle>
    <circle cx="17" cy="12" r="2"></circle>
  </svg>
)

const DimensionsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 3H3v18h18V3z"></path>
    <path d="M21 12H3"></path>
    <path d="M12 3v18"></path>
  </svg>
)

const PolesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="3"></circle>
    <circle cx="12" cy="19" r="3"></circle>
    <line x1="12" y1="8" x2="12" y2="16"></line>
  </svg>
)

const PhysicalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
    <line x1="16" y1="8" x2="2" y2="22"></line>
    <line x1="17.5" y1="15" x2="9" y2="15"></line>
  </svg>
)

const SafetyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
)

// Question mark icon for definitions
const InfoIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
)

// Form Factor Silhouette Icons (futuristic tech design)
const CylindricalSilhouette = ({ size = 60, label }) => (
  <div className="form-factor-silhouette cylindrical">
    <svg width="50" height="70" viewBox="0 0 40 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cylBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2a2a2a"/>
          <stop offset="50%" stopColor="#3d3d3d"/>
          <stop offset="100%" stopColor="#252525"/>
        </linearGradient>
        <linearGradient id="cylCapGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4a4a4a"/>
          <stop offset="100%" stopColor="#2a2a2a"/>
        </linearGradient>
        <filter id="cylGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Body with gradient */}
      <path d="M4 10 L4 46 A16 5 0 0 0 36 46 L36 10" fill="url(#cylBodyGrad)" stroke="#ea5b0c" strokeWidth="1" filter="url(#cylGlow)"/>
      {/* Tech grid lines */}
      <line x1="4" y1="20" x2="36" y2="20" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.4"/>
      <line x1="4" y1="28" x2="36" y2="28" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.4"/>
      <line x1="4" y1="36" x2="36" y2="36" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.4"/>
      {/* Top cap */}
      <ellipse cx="20" cy="10" rx="16" ry="5" fill="url(#cylCapGrad)" stroke="#ea5b0c" strokeWidth="1"/>
      {/* Bottom cap */}
      <ellipse cx="20" cy="46" rx="16" ry="5" fill="url(#cylCapGrad)" stroke="#ea5b0c" strokeWidth="1"/>
      {/* Positive terminal - flat coin/button style in center of top cap */}
      <ellipse cx="20" cy="10" rx="6" ry="2" fill="#ea5b0c" fillOpacity="0.3"/>
      <ellipse cx="20" cy="10" rx="5" ry="1.5" fill="#ea5b0c"/>
      {/* Center indicator line */}
      <line x1="20" y1="15" x2="20" y2="41" stroke="#0d7c99" strokeWidth="0.5" strokeDasharray="2,2" strokeOpacity="0.6"/>
    </svg>
    {label && <span className="silhouette-label">{label}</span>}
  </div>
)

const PrismaticSilhouette = ({ size = 60, label }) => (
  <div className="form-factor-silhouette prismatic">
    <svg width="70" height="70" viewBox="0 0 50 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="prisBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2a2a2a"/>
          <stop offset="50%" stopColor="#3d3d3d"/>
          <stop offset="100%" stopColor="#252525"/>
        </linearGradient>
        <linearGradient id="prisTopGrad" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#3a3a3a"/>
          <stop offset="100%" stopColor="#4a4a4a"/>
        </linearGradient>
        <linearGradient id="prisSideGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3d3d3d"/>
          <stop offset="100%" stopColor="#2a2a2a"/>
        </linearGradient>
        <filter id="prisGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* 3D box - top surface */}
      <path d="M5 12 L40 12 L45 8 L10 8 Z" fill="url(#prisTopGrad)" stroke="#ea5b0c" strokeWidth="1"/>
      {/* Rectangular terminals on top surface - centered on top face */}
      <rect x="14" y="9" width="5" height="2" rx="0.3" fill="#ea5b0c" fillOpacity="0.4"/>
      <rect x="15" y="9.3" width="3" height="1.4" rx="0.2" fill="#ea5b0c"/>
      <rect x="31" y="9" width="5" height="2" rx="0.3" fill="#0d7c99" fillOpacity="0.4"/>
      <rect x="32" y="9.3" width="3" height="1.4" rx="0.2" fill="#0d7c99"/>
      {/* Front face */}
      <rect x="5" y="12" width="35" height="28" rx="1" fill="url(#prisBodyGrad)" stroke="#ea5b0c" strokeWidth="1" filter="url(#prisGlow)"/>
      {/* Tech grid on front */}
      <line x1="5" y1="20" x2="40" y2="20" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.3"/>
      <line x1="5" y1="28" x2="40" y2="28" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.3"/>
      <line x1="22.5" y1="12" x2="22.5" y2="40" stroke="#0d7c99" strokeWidth="0.5" strokeDasharray="2,2" strokeOpacity="0.5"/>
      {/* Side face */}
      <path d="M40 12 L45 8 L45 36 L40 40 Z" fill="url(#prisSideGrad)" stroke="#ea5b0c" strokeWidth="1"/>
    </svg>
    {label && <span className="silhouette-label">{label}</span>}
  </div>
)

const PouchSilhouette = ({ size = 60, label }) => (
  <div className="form-factor-silhouette pouch">
    <svg width="70" height="70" viewBox="0 0 52 45" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="pouchBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2a2a2a"/>
          <stop offset="50%" stopColor="#3d3d3d"/>
          <stop offset="100%" stopColor="#252525"/>
        </linearGradient>
        <filter id="pouchGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Main pouch body */}
      <rect x="6" y="12" width="40" height="28" rx="2" fill="url(#pouchBodyGrad)" stroke="#ea5b0c" strokeWidth="1" filter="url(#pouchGlow)"/>
      {/* Tech grid lines */}
      <line x1="6" y1="19" x2="46" y2="19" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.3"/>
      <line x1="6" y1="26" x2="46" y2="26" stroke="#0d7c99" strokeWidth="0.5" strokeDasharray="3,2" strokeOpacity="0.5"/>
      <line x1="6" y1="33" x2="46" y2="33" stroke="#ea5b0c" strokeWidth="0.3" strokeOpacity="0.3"/>
      <line x1="26" y1="12" x2="26" y2="40" stroke="#0d7c99" strokeWidth="0.5" strokeDasharray="2,2" strokeOpacity="0.4"/>
      {/* Tabs with glow */}
      <rect x="10" y="4" width="8" height="10" rx="1" fill="#ea5b0c" fillOpacity="0.2"/>
      <rect x="11" y="5" width="6" height="8" rx="1" fill="#ea5b0c"/>
      <rect x="34" y="4" width="8" height="10" rx="1" fill="#0d7c99" fillOpacity="0.2"/>
      <rect x="35" y="5" width="6" height="8" rx="1" fill="#0d7c99"/>
      {/* Edge seal lines */}
      <line x1="6" y1="14" x2="46" y2="14" stroke="#555" strokeWidth="0.5" strokeOpacity="0.6"/>
      <line x1="6" y1="38" x2="46" y2="38" stroke="#555" strokeWidth="0.5" strokeOpacity="0.6"/>
    </svg>
    {label && <span className="silhouette-label">{label}</span>}
  </div>
)

// Component to render appropriate silhouette based on shape
const FormFactorSilhouette = ({ shape, label }) => {
  const shapeLC = (shape || '').toLowerCase()
  
  if (shapeLC.includes('cylindrical')) {
    return <CylindricalSilhouette label={label} />
  } else if (shapeLC.includes('prismatic')) {
    return <PrismaticSilhouette label={label} />
  } else if (shapeLC.includes('pouch')) {
    return <PouchSilhouette label={label} />
  }
  
  // Default/unknown shape
  return (
    <div className="form-factor-silhouette unknown">
      <span className="silhouette-unknown">?</span>
      {label && <span className="silhouette-label">{label}</span>}
    </div>
  )
}

// Definition Tooltip Overlay
function DefinitionTooltip({ title, text, isOpen, onClose }) {
  if (!isOpen) return null

  return (
    <div className="definition-tooltip-overlay" onClick={onClose}>
      <div className="definition-tooltip-content" onClick={e => e.stopPropagation()}>
        <div className="definition-tooltip-header">
          <h4>{title}</h4>
          <button className="definition-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="definition-tooltip-body" dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </div>
  )
}

export default function CompareMechanicalView() {
  const [cellDataArray, setCellDataArray] = useState([])
  const [loading, setLoading] = useState(true)
  const [openDefinition, setOpenDefinition] = useState(null)

  // Load full cell data (not minimal, need mechanical details)
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const cellsData = await Promise.all(
          cellIds.map(async (id) => {
            try {
              return await getCellNormalized(id, { fieldGating: 'demo' }) // Compare = browse: server-gate premium for demo
            } catch (err) {
              console.error(`Failed to load cell ${id}:`, err)
              return null
            }
          })
        )
        const validCells = cellsData.filter(Boolean)
        setCellDataArray(validCells)
      } catch (err) {
        console.error('Failed to load compare cells:', err)
      } finally {
        setLoading(false)
      }
    }

    loadCells()
  }, [compareCells.value])

  // Format helpers
  const formatValue = (value, unit = '', decimals = 1) => {
    if (value == null || value === '' || isNaN(value)) return '–'
    return `${Number(value).toFixed(decimals)} ${unit}`.trim()
  }

  const formatValueWithUnit = (value, unit = '', decimals = 1) => {
    if (value == null || value === '' || isNaN(value)) {
      return <span className="spec-value">–</span>
    }
    return (
      <span className="spec-value">
        <span className="value-num">{Number(value).toFixed(decimals)}</span>
        {unit && <span className="value-unit">{unit}</span>}
      </span>
    )
  }

  const formatArrayValueWithUnit = (arr, index, unit = '', decimals = 1) => {
    if (!Array.isArray(arr) || arr[index] == null || isNaN(arr[index])) {
      return <span className="spec-value">–</span>
    }
    return (
      <span className="spec-value">
        <span className="value-num">{Number(arr[index]).toFixed(decimals)}</span>
        {unit && <span className="value-unit">{unit}</span>}
      </span>
    )
  }

  const arePolesDifferent = (arr) => {
    if (!Array.isArray(arr) || arr.length < 2) return false
    return arr[0] !== arr[1]
  }

  if (loading) {
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Loading mechanical data...</div>
      </div>
    )
  }

  if (cellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
          <p className="hint">Select 2-5 cells to compare mechanical properties</p>
        </div>
      </div>
    )
  }

  // Filter out hidden cells
  const allCellDataArray = cellDataArray
  const visibleCellDataArray = allCellDataArray.filter(cell => {
    const cellId = cell.id || cell.product_id || cell.productId
    return !hiddenCells.value.includes(cellId)
  })

  // All cells hidden
  if (visibleCellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
          <p className="hint">Use the eye icon in the compare panel to show cells</p>
        </div>
      </div>
    )
  }

  // Build definitions (compare-specific, no cell-specific values)
  const definitions = {
    dimensions: {
      key: 'dimensions',
      title: COMPARE_MECHANICAL.dimensions?.title || 'Cell Dimensions',
      text: COMPARE_MECHANICAL.dimensions?.text || 'Cell physical dimensions and measurements.'
    },
    poles: {
      key: 'poles', 
      title: COMPARE_MECHANICAL.poleLocations?.title || 'Pole/Tab Dimensions',
      text: COMPARE_MECHANICAL.poleLocations?.text || 'Terminal configuration and dimensions.'
    },
    physical: {
      key: 'physical',
      title: COMPARE_MECHANICAL.physicalProperties?.title || 'Physical Properties',
      text: COMPARE_MECHANICAL.physicalProperties?.text || 'Cell physical characteristics.'
    },
    safety: {
      key: 'safety',
      title: COMPARE_MECHANICAL.safetyFeatures?.title || 'Safety Features',
      text: COMPARE_MECHANICAL.safetyFeatures?.text || 'Built-in protection mechanisms.'
    }
  }

  // Get cell shape info
  const getShapeInfo = (mech) => ({
    isCylindrical: mech?.shape?.toLowerCase().includes('cylindrical'),
    isPouch: mech?.shape?.toLowerCase().includes('pouch'),
    isPrismatic: mech?.shape?.toLowerCase().includes('prismatic')
  })

  // Table format for mechanical comparison
  return (
    <div className="compare-section-view compare-table-view compare-mechanical-table">
      {/* Definition Overlay */}
      <DefinitionTooltip 
        title={definitions[openDefinition]?.title}
        text={definitions[openDefinition]?.text}
        isOpen={!!openDefinition}
        onClose={() => setOpenDefinition(null)}
      />

      {/* Specifications Table */}
      <div className="compare-table-container">
        <table className="compare-table">
          <thead>
            <tr>
              <th className="compare-table-header-label">Property</th>
              {visibleCellDataArray.map(cell => (
                <th key={cell.id} className="compare-table-header-cell">
                  <div className="cell-header-name">{cell.manufacturer}</div>
                  <div className="cell-header-model">{cell.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Form Factor Silhouettes Row (compact visual) */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><FormFactorIcon /></span>
                <span className="section-title">Form Factor</span>
              </td>
            </tr>
            <tr className="form-factor-row">
              <td className="compare-table-label">Type</td>
              {visibleCellDataArray.map((cellData) => (
                <td key={cellData.id} className="compare-table-value form-factor-cell">
                  <FormFactorSilhouette 
                    shape={cellData?.mechanical?.shape}
                    label={cellData?.mechanical?.shape || 'Unknown'}
                  />
                </td>
              ))}
            </tr>

            {/* Cell Dimensions Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><DimensionsIcon /></span>
                <span className="section-title">Cell Dimensions</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'dimensions' ? null : 'dimensions')}
                  title="Show dimensions definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            {/* Cylindrical cells: Diameter/Radius */}
            <tr>
              <td className="compare-table-label">Diameter / Length (x)</td>
              {visibleCellDataArray.map(cell => {
                const mech = cell?.mechanical || {}
                const { isCylindrical } = getShapeInfo(mech)
                return (
                  <td key={cell.id} className="compare-table-value">
                    {isCylindrical 
                      ? (mech.r_cell_mm ? `${(mech.r_cell_mm * 2).toFixed(1)} mm` : '–')
                      : (mech.x_cell_mm ? `${mech.x_cell_mm.toFixed(1)} mm` : '–')
                    }
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">Radius / Width (y)</td>
              {visibleCellDataArray.map(cell => {
                const mech = cell?.mechanical || {}
                const { isCylindrical } = getShapeInfo(mech)
                return (
                  <td key={cell.id} className="compare-table-value">
                    {isCylindrical 
                      ? (mech.r_cell_mm ? `${mech.r_cell_mm.toFixed(2)} mm` : '–')
                      : (mech.y_cell_mm ? `${mech.y_cell_mm.toFixed(1)} mm` : '–')
                    }
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">Height (z)</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.z_cell_mm ? `${cell.mechanical.z_cell_mm.toFixed(1)} mm` : '–'}
                </td>
              ))}
            </tr>

            {/* Pole/Tab Dimensions Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><PolesIcon /></span>
                <span className="section-title">Pole / Tab Dimensions</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'poles' ? null : 'poles')}
                  title="Show pole/tab definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            <tr>
              <td className="compare-table-label">Pole/Tab Location</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.pole_location?.replace(/\s*\([xy]\)/gi, '').trim() || '–'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Pole Radius / Tab Length</td>
              {visibleCellDataArray.map(cell => {
                const mech = cell?.mechanical || {}
                const { isCylindrical } = getShapeInfo(mech)
                return (
                  <td key={cell.id} className="compare-table-value">
                    {isCylindrical 
                      ? (Array.isArray(mech.r_pole_mm) && mech.r_pole_mm[0] != null ? `${mech.r_pole_mm[0].toFixed(2)} mm` : '–')
                      : (Array.isArray(mech.x_pole_mm) && mech.x_pole_mm[0] != null ? `${mech.x_pole_mm[0].toFixed(1)} mm` : '–')
                    }
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">Pole/Tab Height (z)</td>
              {visibleCellDataArray.map(cell => {
                const mech = cell?.mechanical || {}
                return (
                  <td key={cell.id} className="compare-table-value">
                    {Array.isArray(mech.z_pole_mm) && mech.z_pole_mm[0] != null ? `${mech.z_pole_mm[0].toFixed(1)} mm` : '–'}
                  </td>
                )
              })}
            </tr>

            {/* Physical Properties Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><PhysicalIcon /></span>
                <span className="section-title">Physical Properties</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'physical' ? null : 'physical')}
                  title="Show physical properties definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            <tr>
              <td className="compare-table-label">Mass</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.mass_g ? `${cell.mechanical.mass_g.toFixed(1)} g` : '–'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Volume (Cell)</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.vol_cell_cm3 ? `${cell.mechanical.vol_cell_cm3.toFixed(2)} cm³` : '–'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Volume (Installation)</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.vol_install_cm3 ? `${cell.mechanical.vol_install_cm3.toFixed(2)} cm³` : '–'}
                </td>
              ))}
            </tr>
            <tr>
              <td className="compare-table-label">Surface Area</td>
              {visibleCellDataArray.map(cell => (
                <td key={cell.id} className="compare-table-value">
                  {cell?.mechanical?.area_cm2 ? `${cell.mechanical.area_cm2.toFixed(1)} cm²` : '–'}
                </td>
              ))}
            </tr>

            {/* Safety Features Section */}
            <tr className="section-header-row">
              <td colSpan={visibleCellDataArray.length + 1}>
                <span className="section-icon"><SafetyIcon /></span>
                <span className="section-title">Safety Features</span>
                <button 
                  className="section-info-btn" 
                  onClick={() => setOpenDefinition(openDefinition === 'safety' ? null : 'safety')}
                  title="Show safety features definition"
                >
                  <InfoIcon size={14} />
                </button>
              </td>
            </tr>
            <tr>
              <td className="compare-table-label">CID (Current Interrupt Device)</td>
              {visibleCellDataArray.map(cell => {
                if (cell?.general?.internal_analysis_available === false) {
                  return <td key={cell.id} className="compare-table-value analysis-pending">Analysis ongoing</td>
                }
                return (
                  <td key={cell.id} className="compare-table-value">
                    {cell?.safety?.CID ? <span className="safety-yes">✓</span> : <span className="safety-no">✗</span>}
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">PTC (Positive Temp. Coefficient)</td>
              {visibleCellDataArray.map(cell => {
                if (cell?.general?.internal_analysis_available === false) {
                  return <td key={cell.id} className="compare-table-value analysis-pending">–</td>
                }
                return (
                  <td key={cell.id} className="compare-table-value">
                    {cell?.safety?.PTC ? <span className="safety-yes">✓</span> : <span className="safety-no">✗</span>}
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">ISP (Internal Short Protection)</td>
              {visibleCellDataArray.map(cell => {
                if (cell?.general?.internal_analysis_available === false) {
                  return <td key={cell.id} className="compare-table-value analysis-pending">–</td>
                }
                return (
                  <td key={cell.id} className="compare-table-value">
                    {cell?.safety?.ISP ? <span className="safety-yes">✓</span> : <span className="safety-no">✗</span>}
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">VENT (Pressure Relief Vent)</td>
              {visibleCellDataArray.map(cell => {
                if (cell?.general?.internal_analysis_available === false) {
                  return <td key={cell.id} className="compare-table-value analysis-pending">–</td>
                }
                return (
                  <td key={cell.id} className="compare-table-value">
                    {cell?.safety?.VENT ? <span className="safety-yes">✓</span> : <span className="safety-no">✗</span>}
                  </td>
                )
              })}
            </tr>
            <tr>
              <td className="compare-table-label">BURST (Burst Disc)</td>
              {visibleCellDataArray.map(cell => {
                if (cell?.general?.internal_analysis_available === false) {
                  return <td key={cell.id} className="compare-table-value analysis-pending">–</td>
                }
                return (
                  <td key={cell.id} className="compare-table-value">
                    {cell?.safety?.BURST ? <span className="safety-yes">✓</span> : <span className="safety-no">✗</span>}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
